// React
import { ReactElement, ReactNode, useEffect, useState } from 'react';
// Next
import Head from 'next/head';
import { useRouter } from 'next/router';
// Modules
import classNames from 'classnames/bind';
import ResizeObserver from 'react-resize-observer';
import { SkipNavContent, SkipNavLink } from '@reach/skip-nav';
import { Slide, ToastContainer } from 'react-toastify';
// Layout
import { MobileSkeleton } from '@utmb/features/layout/navigation-public/MobileSkeleton';
import { NotAttachedRunner } from '@utmb/layouts/my-utmb/NotAttachedRunner';
// Features
import JsonLd from '@utmb/features/seo/JsonLd';
import DashboardSkeleton from '@utmb/features/my-utmb/dashboard-skeleton/DashboardSkeleton';
import { Footer } from '@utmb/features/footer/footer';
import { Mobile } from '@utmb/features/layout/navigation-public/mobile';
import { TopBar } from '@utmb/features/layout/navigation-public/desktop-utils/top-bar';
// Design System
import Navigation from '@utmb/design-system/my-utmb-navigation/Navigation';
import { NavigationSkeleton } from '@utmb/design-system/my-utmb-navigation/NavigationSkeleton';
// Contexts/Hooks
import { useCloudinary } from '@utmb/design-system/use-cloudinary/UseCloudinary';
import { useDashboard } from '@utmb/contexts/DashboardContext';
import { useDashboardNavigation } from '@utmb/design-system/use-dashboard-navigation/UseDashboardNavigation';
// Interfaces
import {
    BreadcrumbsView,
    GlobalEventView,
    GlobalFooterView,
    GlobalMobileBottomBarView,
    GlobalNavigationView,
    EventView,
    GlobalPartnersView,
    SeoView,
    GlobalNavigationMenuLevelView,
} from '@utmb/types/index';
// Styles
import styles from './layout-base.module.scss';
import 'react-toastify/dist/ReactToastify.css';
import { LoyaltyLevelEnum } from '@utmb/types/enum/LoyaltyLevelEnum';
import { getColorStyle } from '@utmb/utils/colors';
import { ColorTypesEnum } from '@utmb/types/colors';
import { useUser } from '@utmb/contexts/AuthContext';
import { dashboardTour } from '@utmb/features/dashboard-tour/dashboard-tour';
import { useTranslation } from 'next-i18next';

const cx = classNames.bind(styles);
const LOCAL_STORAGE_TOUR = 'dashboardTourDisplayed';

export interface LayoutProps extends SeoView {
    breadcrumbs?: BreadcrumbsView;
    children: ReactNode;
    event: GlobalEventView;
    eventsTopBar: EventView[];
    languages: string[];
    footer: GlobalFooterView;
    isMobile: boolean;
    landing?: boolean;
    mobileBottomBar: GlobalMobileBottomBarView;
    mobileHeader?: boolean;
    navigation?: GlobalNavigationView;
    raceTheme?: string;
    raceThemeIsDark?: boolean;
    translationSlugs?: {
        [key: string]: string;
    };
    partners?: GlobalPartnersView;
    menuLogoIsDark?: boolean;
}

const LayoutBase = (props: LayoutProps): ReactElement => {
    const {
        breadcrumbs,
        children,
        description,
        event,
        eventsTopBar,
        footer,
        image,
        keywords,
        languages,
        menuLogoIsDark,
        mobileBottomBar,
        partners,
        title,
        translationSlugs,
    } = props;

    const [pageUrl, setPageUrl] = useState('');
    const [isMobile, setIsMobile] = useState(false);
    const { getImage } = useCloudinary();
    const { isLoginMemberInProgress, isAttached, dashboardOrganizer } = useDashboard();
    const { me } = useUser();
    const { dashboardNavigation } = useDashboardNavigation();
    const user = useUser();
    const { t } = useTranslation('myutmb');
    const { t: tc } = useTranslation('common');

    // Reset Types for navigation
    const dashboardRoutes = dashboardNavigation as unknown as GlobalNavigationMenuLevelView[];

    const isLoading = isLoginMemberInProgress || dashboardNavigation === undefined;

    const mobileNavigation: GlobalNavigationView = {
        children: dashboardRoutes || [],
    };

    const navigationProps = {
        breadcrumbs,
        mobileBottomBar,
        navigation: mobileNavigation,
        event,
        eventsTopBar,
        languages,
        translationSlugs,
        partners,
        menuLogoIsDark,
    };

    useEffect(() => {
        setPageUrl(window.location.href);
    }, []);

    useEffect(() => {
        if (localStorage.getItem(LOCAL_STORAGE_TOUR) !== 'true' && !isLoading) {
            localStorage.setItem(LOCAL_STORAGE_TOUR, 'true');
            dashboardTour(t, tc);
        }
    }, [isLoading]);

    useEffect(() => {
        if (me?.loyalty) {
            document.documentElement.style.setProperty(
                '--utmb-color-loyalty-current-level',
                me.loyalty === LoyaltyLevelEnum.LEVEL1
                    ? 'var(--utmb-color-loyalty-level1)'
                    : me?.loyalty === LoyaltyLevelEnum.LEVEL2
                    ? 'var(--utmb-color-loyalty-level2)'
                    : me?.loyalty === LoyaltyLevelEnum.LEVEL3
                    ? 'var(--utmb-color-loyalty-level3)'
                    : getColorStyle(ColorTypesEnum.WS_SECONDARY)
            );
        } else {
            document.documentElement.style.setProperty('--utmb-color-loyalty-current-level', getColorStyle(ColorTypesEnum.WS_SECONDARY));
        }
    }, [me]);

    const imageURL = getImage(image?.publicId);

    //const containerCSS = cx(/*'container-xxl', 'd-flex', 'flex-column', 'flex-lg-row', */ styles.container);

    const dashboardContentCSS = cx(styles.dashboard_content, {
        dashboard_content: !isLoading,
        dashboard_content_skeleton: isLoading,
    });
    const isAdmin = user?.jwt?.realm_access?.roles?.find((val) => val.includes('admin'));
    const containerCSS = cx(styles.container, isAdmin ? styles.isAdmin : undefined);

    const mobileContent =
        isAttached || dashboardOrganizer ? (
            children
        ) : (
            <>
                <NotAttachedRunner />
                {children}
            </>
        );

    const { locale } = useRouter();

    return (
        <>
            <Head>
                <title>{title}</title>
                {/* <meta name="robots" content="noindex" /> */}
                <meta name="title" content={title} />
                <meta name="description" content={description} />
                <meta name="keywords" content={keywords} />
                <meta name="og:title" content={title} />
                <meta name="og:description" content={description} />
                <meta name="og:url" content={pageUrl} />
                <meta name="og:image" content={imageURL} />
                <meta name="og:type" content="website" />
                <meta name="og:locale" content={locale} />
                <meta name="twitter:site" content="@UTMBWorldSeries" />
                <meta name="twitter:url" content={pageUrl} />
                <meta name="twitter:title" content={title} />
                <meta name="twitter:description" content={description} />
                <meta name="twitter:image" content={imageURL} />
                <meta name="twitter:card" content="summary_large_image" />
                <link rel="canonical" href={pageUrl} />
            </Head>
            <JsonLd />
            <SkipNavLink>Skip to Content</SkipNavLink>
            <ResizeObserver
                onResize={() => {
                    const isWindowMobile = window.innerWidth < parseInt(styles['bp-lg'], 10);
                    setIsMobile(isWindowMobile);
                }}
            />

            {isMobile ? (
                <>{isLoading ? <MobileSkeleton /> : <Mobile isMyUtmb {...navigationProps} />}</>
            ) : (
                <TopBar eventName={event.name} eventsTopBar={eventsTopBar} languages={languages} translationSlugs={translationSlugs} />
            )}

            <div className={containerCSS}>
                {isMobile ? (
                    <>{isLoading ? <DashboardSkeleton /> : mobileContent}</>
                ) : (
                    <>
                        {isLoading ? <NavigationSkeleton /> : <Navigation />}
                        <div className={dashboardContentCSS}>
                            {isLoading ? (
                                <DashboardSkeleton />
                            ) : (
                                <>
                                    {isAttached || dashboardOrganizer ? (
                                        <SkipNavContent>{children}</SkipNavContent>
                                    ) : (
                                        <>
                                            <NotAttachedRunner />
                                            {children}
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>
            <Footer {...footer} event={event} />
            <ToastContainer position="bottom-right" hideProgressBar transition={Slide} />
        </>
    );
};

export default LayoutBase;
